import { useCallback, useEffect, useState } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDateTimeUtc } from '../lib/util';
import { ApiProblem } from '../api/client';
import {
  deleteEnterprise,
  getAcceptanceEvidence,
  pullCdrsNow,
  type AcceptanceEvidence,
  type CdrChain,
  type CdrPullResult,
  type CdrPullRun,
} from '../api/settlement';

// #108 — Settlement operations (admin-only). Dual-use, not a test-only panel:
// the CDR "pull now" and enterprise-delete are the standing incident-recovery /
// offboard / cleanup operations, and the collapsible §6 acceptance section is a
// lens over the same operations (evidence table + preset buttons that invoke
// them with §6 parameters + copy-as-Markdown for the CTIA submission).
//
// The server enforces admin-only (403 otherwise); the page renders this only
// for user.canAdmin as a UX guard. Nothing here is gated to an "acceptance
// mode" — it's the operations surface an admin needs day-to-day.

const CHAIN_LABEL: Record<CdrChain, string> = { test: 'Test endpoint', prod: 'Production endpoint' };

function pill(text: string, color: string) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
      color, border: `1px solid ${color}`, whiteSpace: 'nowrap',
    }}>{text}</span>
  );
}

function outcomeColor(outcome: string): string {
  if (outcome === 'SUCCESS' || outcome === 'ADOPTED') return C.success;
  if (outcome === 'THROTTLED' || outcome === 'BUSY' || outcome === 'SKIPPED') return C.warn;
  return C.danger; // FAILED
}

// Pull-history table shared by the Operations and the acceptance sections.
function RunTable({ runs }: { runs: CdrPullRun[] }) {
  if (runs.length === 0) {
    return <div style={{ fontSize: 12, color: C.textMuted }}>No pull runs recorded yet.</div>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={styles.table}>
        <thead><tr>
          <th style={styles.th}>Started</th>
          <th style={styles.th}>Chain</th>
          <th style={styles.th}>Trigger</th>
          <th style={styles.th}>Outcome</th>
          <th style={styles.th}>Fetched / Ingested</th>
          <th style={styles.th}>Pages</th>
          <th style={styles.th}>Cursor before → after</th>
        </tr></thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.id}>
              <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{fmtDateTimeUtc(r.startedAt)}</td>
              <td style={styles.td}>{r.chain}</td>
              <td style={styles.td}>{r.triggeredBy}</td>
              <td style={styles.td}>
                {pill(r.outcome, outcomeColor(r.outcome))}
                {r.detail && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{r.detail}</div>}
              </td>
              <td style={styles.td}>{r.fetched ?? '—'} / {r.ingested ?? '—'}</td>
              <td style={styles.td}>{r.pages ?? '—'}</td>
              <td style={styles.td}>
                <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
                  {r.cursorBefore ?? '—'} → {r.cursorAfter ?? '—'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function field(label: string, value: React.ReactNode) {
  return (
    <div>
      <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: C.text }}>{value}</div>
    </div>
  );
}

// The §6 checklist derived from the evidence package. Each item's status pill
// reflects whether the evidencing data is present.
function AcceptanceChecklist({ ev }: { ev: AcceptanceEvidence }) {
  const hasRef = ev.referenceCases.length > 0;
  const hasProdPull = ev.latestProdRun !== null;
  const cronSuccesses = ev.recentRuns.filter((r) => r.outcome === 'SUCCESS' && r.triggeredBy === 'cron');
  const hasRecurring = cronSuccesses.length >= 2;
  const hasMultiPage = ev.latestMultiPageRun !== null;
  const hasDelete = ev.recentDeletes.length > 0;

  const items: Array<{ id: string; label: string; done: boolean; evidence: string }> = [
    { id: 'OA-1', label: 'Enterprise registration (reference registrant)', done: hasRef,
      evidence: hasRef ? `EID ${ev.referenceCases[0].eid}` : 'no fully-synced reference case found' },
    { id: 'OA-2', label: 'Enterprise delete / offboard', done: hasDelete,
      evidence: hasDelete ? `last delete of ${ev.recentDeletes[0].eid ?? '—'}` : 'no delete recorded' },
    { id: 'OA-3', label: 'Production CDR pull (fresh agent)', done: hasProdPull,
      evidence: hasProdPull ? `${ev.latestProdRun!.outcome} at ${fmtDateTimeUtc(ev.latestProdRun!.startedAt)}` : 'no prod pull yet' },
    { id: 'OA-4', label: 'Recurring CDR retrieval (≥2 cron pulls ≥15 min apart)', done: hasRecurring,
      evidence: `${cronSuccesses.length} cron successes in recent runs` },
    { id: 'OA-5', label: 'CDR pull with limited max_count (multi-page)', done: hasMultiPage,
      evidence: hasMultiPage ? `max_count ${ev.latestMultiPageRun!.maxCount}, ${ev.latestMultiPageRun!.pages} pages` : 'no small-batch multi-page pull yet' },
    { id: 'VA-1', label: 'DIR upload (reference registrant)', done: hasRef,
      evidence: hasRef ? `DIRID ${ev.referenceCases[0].dirid}` : 'no reference case' },
    { id: 'VA-2', label: 'TN records upload (reference registrant)', done: hasRef,
      evidence: hasRef ? `${ev.referenceCases[0].deliveredTnCount} TNs delivered` : 'no reference case' },
  ];

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={styles.table}>
        <thead><tr>
          <th style={styles.th}>§6 item</th>
          <th style={styles.th}>Requirement</th>
          <th style={styles.th}>Status</th>
          <th style={styles.th}>Evidence</th>
        </tr></thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id}>
              <td style={styles.td}><strong>{it.id}</strong></td>
              <td style={styles.td}>{it.label}</td>
              <td style={styles.td}>{it.done ? pill('EVIDENCED', C.success) : pill('PENDING', C.warn)}</td>
              <td style={styles.td}><span style={{ fontSize: 12, color: C.textMuted }}>{it.evidence}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function evidenceMarkdown(ev: AcceptanceEvidence): string {
  const lines: string[] = [];
  lines.push('# CTIA §6 Acceptance Evidence — 382 BCID');
  lines.push('');
  lines.push(`- Mode: **${ev.mode}**  ·  Endpoint host: \`${ev.endpointHost ?? '(none)'}\`  ·  CDR test mode: ${ev.cdrTestMode}`);
  lines.push(`- VAID: \`${ev.vaid ?? '(none)'}\``);
  lines.push(`- Identity model: ${ev.saidNote}`);
  lines.push('');
  lines.push('## Cursor state');
  for (const c of ev.cursors) lines.push(`- \`${c.id}\` = ${c.lastTime} (updated ${c.updatedAt})`);
  lines.push('');
  lines.push('## Reference registrant (OA-1 / VA-1 / VA-2)');
  for (const r of ev.referenceCases) {
    lines.push(`- ${r.caseId} — ${r.registrant}: EID \`${r.eid}\`, DIRID \`${r.dirid}\`, ${r.deliveredTnCount} TNs delivered, last sync ${r.lastSyncAt ?? '—'}`);
  }
  lines.push('');
  lines.push('## Enterprise deletes (OA-2)');
  for (const d of ev.recentDeletes) lines.push(`- ${d.at}: EID \`${d.eid ?? '—'}\` (source: ${d.source ?? '—'})`);
  lines.push('');
  const runLine = (r: CdrPullRun) =>
    `- ${r.startedAt} [${r.chain}/${r.triggeredBy}] ${r.outcome}: fetched ${r.fetched ?? '—'}, ingested ${r.ingested ?? '—'}, ${r.pages ?? '—'} pages, cursor ${r.cursorBefore ?? '—'}→${r.cursorAfter ?? '—'}`;
  lines.push('## CDR pulls (OA-3 prod / OA-4 recurring / OA-5 multi-page)');
  if (ev.latestProdRun) lines.push(`OA-3 (latest prod): ${runLine(ev.latestProdRun).slice(2)}`);
  lines.push('OA-4 (recent runs, both chains):');
  for (const r of ev.recentRuns) lines.push(runLine(r));
  if (ev.latestMultiPageRun) lines.push(`OA-5 (latest small-batch multi-page): ${runLine(ev.latestMultiPageRun).slice(2)}`);
  return lines.join('\n');
}

export function SettlementOpsPanel() {
  const [ev, setEv] = useState<AcceptanceEvidence | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [showAcceptance, setShowAcceptance] = useState(false);
  const [copied, setCopied] = useState(false);

  // Pull-now form.
  const [pullChain, setPullChain] = useState<CdrChain>('test');
  const [pullMax, setPullMax] = useState('');
  // Delete-enterprise form.
  const [delEid, setDelEid] = useState('');
  const [delForce, setDelForce] = useState(false);
  const [delConfirm, setDelConfirm] = useState('');

  const load = useCallback(() => {
    getAcceptanceEvidence()
      .then((e) => { setEv(e); setError(null); })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const describePull = (r: CdrPullResult): string => {
    if (r.outcome === 'SUCCESS') return `${r.chain}: fetched ${r.fetched}, ingested ${r.ingested}, ${r.pages} page(s), cursor ${r.cursorBefore}→${r.cursorAfter}${r.legacyAdopted ? ' (adopted legacy cursor)' : ''}`;
    if (r.outcome === 'ADOPTED') return `${r.chain}: self-healed to disclosed cursor ${r.cursorAfter}`;
    if (r.outcome === 'SKIPPED') return `${r.chain}: skipped (stub mode / non-retriable)`;
    return `${r.chain}: ${r.outcome}`;
  };

  const runPull = useCallback((chain: CdrChain, maxCount?: number) => {
    const label = maxCount !== undefined ? `${chain} chain (max_count ${maxCount})` : `${chain} chain`;
    if (!window.confirm(
      `Pull CDRs now on the ${label}?\n\nThis advances the ${chain} cursor chain and respects the 15-minute cadence floor. It is chain-safe (it never sends an arbitrary cursor), but it consumes a real retrieval against the platform.`,
    )) return;
    setBusy(true);
    setActionMsg(null);
    pullCdrsNow(chain, maxCount)
      .then((r) => setActionMsg({ ok: true, text: `Pull complete — ${describePull(r)}` }))
      .catch((err: unknown) => {
        if (err instanceof ApiProblem && err.status === 429) {
          setActionMsg({ ok: false, text: `Throttled — within the cadence window. ${err.detail ?? ''}` });
        } else if (err instanceof ApiProblem && err.status === 409) {
          setActionMsg({ ok: false, text: `Busy — a ${chain} pull is already running.` });
        } else {
          setActionMsg({ ok: false, text: err instanceof Error ? err.message : String(err) });
        }
      })
      .finally(() => { setBusy(false); load(); });
  }, [load]);

  const runDelete = useCallback(() => {
    if (delConfirm !== delEid) {
      setActionMsg({ ok: false, text: 'Type the EID in the confirmation box to delete.' });
      return;
    }
    if (!window.confirm(
      `Delete platform enterprise ${delEid}?\n\nThis removes the enterprise account on the CTIA platform (cascading its DIRs and TN records)${delForce ? ' and force-resets a live case bound to it' : ''}. This is not reversible on the platform side.`,
    )) return;
    setBusy(true);
    setActionMsg(null);
    deleteEnterprise(delEid, delForce)
      .then((r) => {
        setActionMsg({ ok: true, text: `Deleted enterprise ${r.eid}${r.caseReset ? ` — reset case ${r.caseReset}` : ''}.` });
        setDelEid(''); setDelConfirm(''); setDelForce(false);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiProblem && err.status === 409) {
          setActionMsg({ ok: false, text: `Blocked — ${err.detail ?? 'the EID is bound to a live case; enable Force to delete anyway.'}` });
        } else {
          setActionMsg({ ok: false, text: err instanceof Error ? err.message : String(err) });
        }
      })
      .finally(() => { setBusy(false); load(); });
  }, [delEid, delForce, delConfirm, load]);

  const copyEvidence = useCallback(() => {
    if (!ev) return;
    const md = evidenceMarkdown(ev);
    navigator.clipboard.writeText(md).then(
      () => { setCopied(true); window.setTimeout(() => setCopied(false), 2000); },
      () => setActionMsg({ ok: false, text: 'Clipboard write failed.' }),
    );
  }, [ev]);

  const sectionHeader = (text: string) => (
    <h3 style={{ fontSize: 14, fontWeight: 600, color: C.text, margin: '0 0 12px' }}>{text}</h3>
  );

  return (
    <div style={{ ...styles.card, background: C.bgInput, marginTop: 16 }} data-testid="settlement-ops-panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700 }}>Settlement operations</h3>
        <span style={{ fontSize: 11, color: C.textMuted }}>admin only</span>
      </div>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>
        Standing platform operations: pull CDRs on either cursor chain (incident recovery, post-flip verification), delete a platform enterprise (offboard / cleanup), and review the full pull history. The CTIA §6 acceptance checklist below is a lens over these same operations.
      </div>

      {error && <div style={{ fontSize: 12, color: C.danger, marginBottom: 12 }}>Evidence unavailable: {error}</div>}
      {actionMsg && (
        <div style={{ marginBottom: 12, fontSize: 12, color: actionMsg.ok ? C.success : C.danger }} data-testid="ops-action-msg">
          {actionMsg.ok ? '✓ ' : '✗ '}{actionMsg.text}
        </div>
      )}

      {/* Operations (always relevant) */}
      <div style={{ marginBottom: 24 }}>
        {sectionHeader('Pull CDRs now')}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Cursor chain</div>
            <select
              style={{ ...styles.input, width: 200 }}
              value={pullChain}
              onChange={(e) => setPullChain(e.target.value as CdrChain)}
              data-testid="pull-chain"
            >
              <option value="test">{CHAIN_LABEL.test}</option>
              <option value="prod">{CHAIN_LABEL.prod}</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>max_count (optional)</div>
            <input
              style={{ ...styles.input, width: 140 }}
              value={pullMax}
              onChange={(e) => setPullMax(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="1–1000"
              data-testid="pull-maxcount"
            />
          </div>
          <button
            style={{ ...styles.btn, ...styles.btnGold }}
            disabled={busy}
            onClick={() => runPull(pullChain, pullMax === '' ? undefined : Number(pullMax))}
            data-testid="pull-now-btn"
          >{busy ? 'Working…' : 'Pull now'}</button>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        {sectionHeader('Delete platform enterprise')}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>EID</div>
            <input
              style={{ ...styles.input, width: 260, fontFamily: 'monospace' }}
              value={delEid}
              onChange={(e) => setDelEid(e.target.value.trim())}
              placeholder="enterprise id"
              data-testid="del-eid"
            />
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Type the EID to confirm</div>
            <input
              style={{ ...styles.input, width: 260, fontFamily: 'monospace' }}
              value={delConfirm}
              onChange={(e) => setDelConfirm(e.target.value.trim())}
              placeholder="repeat the EID"
              data-testid="del-confirm"
            />
          </div>
          <label style={{ fontSize: 12, color: C.text, display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8 }}>
            <input type="checkbox" checked={delForce} onChange={(e) => setDelForce(e.target.checked)} data-testid="del-force" />
            Force (delete even if bound to a live case)
          </label>
          <button
            style={{ ...styles.btn, ...styles.btnDanger }}
            disabled={busy || delEid === '' || delConfirm !== delEid}
            onClick={runDelete}
            data-testid="del-btn"
          >Delete enterprise</button>
        </div>
      </div>

      <div style={{ marginBottom: 8 }}>
        {sectionHeader('Pull history')}
        <RunTable runs={ev ? ev.recentRuns : []} />
      </div>

      {/* CTIA §6 acceptance (collapsible) */}
      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 16, paddingTop: 16 }}>
        <button
          style={{ ...styles.btn, ...styles.btnOutline }}
          onClick={() => setShowAcceptance((s) => !s)}
          data-testid="toggle-acceptance"
        >{showAcceptance ? 'Hide' : 'Show'} CTIA §6 acceptance checklist</button>

        {showAcceptance && ev && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              {field('Mode', ev.mode)}
              {field('Endpoint host', ev.endpointHost ?? '—')}
              {field('CDR test mode', String(ev.cdrTestMode))}
              {field('VAID', ev.vaid ?? '—')}
            </div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 16 }}>{ev.saidNote}</div>

            <AcceptanceChecklist ev={ev} />

            <div style={{ marginTop: 16, marginBottom: 12 }}>
              {sectionHeader('Run §6 steps')}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} disabled={busy}
                  onClick={() => runPull('prod')} data-testid="preset-oa3">OA-3: prod-chain pull</button>
                <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} disabled={busy}
                  onClick={() => runPull('test', 10)} data-testid="preset-oa5">OA-5: test-chain pull (max_count 10)</button>
              </div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>
                OA-4 is evidenced by two cron pulls ≥15 minutes apart (recorded automatically; the cron pulls the prod chain since the 2026-07-16 endpoint flip). Use OA-2 by deleting a staging enterprise above.
              </div>
            </div>

            <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={copyEvidence} data-testid="copy-evidence">
              {copied ? 'Copied!' : 'Copy evidence as Markdown'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
